import { fromEvent } from 'rxjs';
import { filter } from 'rxjs/operators';

import { Injectable } from '@angular/core';

import { Focus } from '@models/account/focus.model';
import { ContractFilter } from '@models/contract-filter';
import { AppTheme, AppVue } from '@models/types/app-theme';

type StorageKeyEnum = 'focus' | 'vue' | 'account' | 'closeCookiesPolicyWarning' | 'contract-filter' | 'theme' | 'lang';
const StorageKeyEnum = {
  FOCUS: 'focus' as StorageKeyEnum,
  ACCOUNT: 'account' as StorageKeyEnum,
  CLOSE_COOKIES_POLICY_WARNING: 'closeCookiesPolicyWarning' as StorageKeyEnum,
  CONTRACT_FILTER: 'contract-filter' as StorageKeyEnum,
  VUE: 'vue' as StorageKeyEnum,
  THEME: 'theme' as StorageKeyEnum,
  LANG: 'lang' as StorageKeyEnum,
};

@Injectable({
  providedIn: 'root',
})
export class LocalStorageService {
  private storageInstance = localStorage;
  private prefix = 'wattson-front';
  public storageVueChange$ = fromEvent(window, 'storage').pipe(
    filter((event: StorageEvent) => event.key === this.getFullKey(StorageKeyEnum.VUE)),
  );

  clear() {
    return this.storageInstance.clear();
  }

  private getFullKey(key: StorageKeyEnum) {
    return `${this.prefix}.${key}`;
  }

  private set(key: StorageKeyEnum, value: unknown) {
    this.storageInstance.setItem(this.getFullKey(key), typeof value === 'string' ? value : JSON.stringify(value));
  }

  private get<T = string>(key: StorageKeyEnum): T | undefined {
    const value = this.storageInstance.getItem(this.getFullKey(key));
    if (value) {
      try {
        return JSON.parse(value) as T;
      } catch {
        return value as T;
      }
    }
    return undefined;
  }

  remove(keys: StorageKeyEnum[]): void {
    return keys.forEach((key) => this.storageInstance.removeItem(this.getFullKey(key)));
  }

  //
  get localAccountId() {
    return this.get<number>(StorageKeyEnum.ACCOUNT);
  }

  set localAccountId(accountId: number) {
    this.set(StorageKeyEnum.ACCOUNT, `${accountId}`);
  }

  get focus() {
    return this.get<Record<number, Focus>>(StorageKeyEnum.FOCUS);
  }

  set focus(value: Record<number, Focus>) {
    this.set(StorageKeyEnum.FOCUS, value);
  }

  get closeCookiesWarning() {
    return this.get<boolean>(StorageKeyEnum.CLOSE_COOKIES_POLICY_WARNING);
  }

  set closeCookiesWarning(value: boolean) {
    this.set(StorageKeyEnum.CLOSE_COOKIES_POLICY_WARNING, value);
  }

  get contractFilter() {
    return this.get<Record<number, ContractFilter>>(StorageKeyEnum.CONTRACT_FILTER);
  }

  set contractFilter(value: Record<number, ContractFilter>) {
    this.set(StorageKeyEnum.CONTRACT_FILTER, value);
  }

  get lang() {
    return this.get<string>(StorageKeyEnum.LANG);
  }

  set lang(value: string) {
    this.set(StorageKeyEnum.LANG, value);
  }

  get vue() {
    return this.get<AppVue>(StorageKeyEnum.VUE);
  }

  set vue(value: AppVue) {
    this.set(StorageKeyEnum.VUE, value);
  }

  get theme() {
    return this.get<AppTheme>(StorageKeyEnum.THEME);
  }

  set theme(value: AppTheme) {
    this.set(StorageKeyEnum.THEME, value);
  }

  get isAdmin() {
    return this.vue === 'business';
  }

  hasLocalPreferences() {
    return this.focus && Object.keys(this.focus).length > 0;
  }
}
